import {
  BRIDGE_MESSAGE_SOURCES,
  BRIDGE_MESSAGE_TARGETS,
  createBridgeEnvelope,
} from "@bezoned/desktop-bridge/bridge-common.js";
import { isBridgeMessageTargetForFrontend } from "@bezoned/desktop-bridge/bridge-predicates.js";
import {
  DESKTOP_AVATAR_MENU_ACTIONS,
  DESKTOP_AVATAR_MENU_MESSAGE_TYPES,
  isDesktopAvatarMenuBridgeMessage,
} from "@bezoned/desktop-bridge/desktop-avatar-menu-messaging.js";
import type {
  DesktopAvatarMenuActionPayload,
  DesktopAvatarMenuPayload,
  DesktopAvatarMenuRequestPayload,
} from "@bezoned/desktop-bridge/desktop-avatar-menu-messaging.js";
import type { JsonMessagePayload } from "@bezoned/desktop-bridge/events.js";
import { isDemoUserId } from "@bezoned/shared/utils/demo-users.js";
import { useCallback, useEffect, useRef } from "react";

import { useTeamsNavigation } from "../../hooks/use-teams-navigation.js";
import {
  getAvatarMenuInfo,
  sendChatMessage,
} from "../../services/api.service.js";
import { useUserImageStore } from "../../store/user-image.store.js";
import { recordErrorInSentry } from "../../utils/init-sentry.js";
import { useAuth } from "../auth/use-auth.js";
import { getUser, getUserPresenceFromFluid } from "../fluid/fluid.service.js";
import { useFluid } from "../fluid/hooks/use-fluid.js";
import { useDesktopBridgeRoom } from "./use-desktop-bridge-room.js";
import { useDesktopBridge } from "./use-desktop-bridge.js";

const getAvatarMenuRequest = ({
  payload,
  bridgeRoom,
}: {
  payload: JsonMessagePayload;
  bridgeRoom: string;
}): DesktopAvatarMenuRequestPayload | null => {
  if (payload.room !== bridgeRoom) {
    return null;
  }

  if (!isDesktopAvatarMenuBridgeMessage(payload.data)) {
    return null;
  }

  if (payload.data.type !== DESKTOP_AVATAR_MENU_MESSAGE_TYPES.REQUEST) {
    return null;
  }

  if (
    payload.data.source !== BRIDGE_MESSAGE_SOURCES.DESKTOP ||
    !isBridgeMessageTargetForFrontend(payload.data.target)
  ) {
    return null;
  }

  return payload.data.payload;
};

const getAvatarMenuAction = ({
  payload,
  bridgeRoom,
}: {
  payload: JsonMessagePayload;
  bridgeRoom: string;
}): DesktopAvatarMenuActionPayload | null => {
  if (payload.room !== bridgeRoom) {
    return null;
  }

  if (!isDesktopAvatarMenuBridgeMessage(payload.data)) {
    return null;
  }

  if (payload.data.type !== DESKTOP_AVATAR_MENU_MESSAGE_TYPES.ACTION) {
    return null;
  }

  if (
    payload.data.source !== BRIDGE_MESSAGE_SOURCES.DESKTOP ||
    !isBridgeMessageTargetForFrontend(payload.data.target)
  ) {
    return null;
  }

  return payload.data.payload;
};

const resolvePictureUrl = (userId: string): string | null =>
  useUserImageStore.getState().getPhoto(userId);

const toParticipantIdentifierSet = ({
  selectedUserIdentifier,
  currentUserIdentifier,
}: {
  selectedUserIdentifier: string | null;
  currentUserIdentifier: string;
}): string[] =>
  [...new Set([selectedUserIdentifier, currentUserIdentifier])].filter(
    (value): value is string =>
      value !== null && value !== undefined && value.length > 0
  );

export const DesktopBridgeAvatarMenuSyncListener = () => {
  const { sharedTree } = useFluid();
  const { user } = useAuth();
  const { navigateToChat, navigateToCall } = useTeamsNavigation({
    onCompleted: () => null,
  });
  const {
    status: bridgeStatus,
    instanceId,
    sendJson,
    onJsonMessage,
  } = useDesktopBridge();
  const bridgeRoom = useDesktopBridgeRoom();
  const pendingRequestsRef = useRef<
    Map<string, Promise<DesktopAvatarMenuPayload>>
  >(new Map());

  const resolveAvatarMenuPayload = useCallback(
    (userId: string): Promise<DesktopAvatarMenuPayload> => {
      const cached = pendingRequestsRef.current.get(userId);
      if (cached) {
        return cached;
      }

      const resolver = (async () => {
        const fluidUser = getUser({ sharedTree, userId });
        const presence = getUserPresenceFromFluid({ sharedTree, userId });

        const [pictureUrl, avatarMenuInfo] = await Promise.all([
          Promise.resolve(resolvePictureUrl(userId)),
          getAvatarMenuInfo({ msUserId: userId }).catch((error: unknown) => {
            recordErrorInSentry(error);
            return null;
          }),
        ]);

        return {
          availability: presence?.availability ?? null,
          displayName:
            avatarMenuInfo?.info?.displayName ?? fluidUser?.name ?? null,
          email: avatarMenuInfo?.info?.email ?? null,
          events: avatarMenuInfo?.events ?? null,
          outOfOffice: presence?.outOfOffice?.isOutOfOffice ?? false,
          phone: avatarMenuInfo?.info?.phone ?? null,
          pictureUrl,
          statusMessage: presence?.statusMessage ?? "",
          teamsUserIdentifier:
            avatarMenuInfo?.info?.teamsUserIdentifier ?? null,
          timeZone: avatarMenuInfo?.timeZone ?? null,
          userId,
        };
      })();

      pendingRequestsRef.current.set(userId, resolver);

      return resolver.finally(() => {
        pendingRequestsRef.current.delete(userId);
      });
    },
    [sharedTree]
  );

  const sendAvatarMenuUpdate = useCallback(
    (menu: DesktopAvatarMenuPayload) => {
      if (!(bridgeStatus === "ready" && instanceId)) {
        return;
      }

      sendJson(
        bridgeRoom,
        createBridgeEnvelope({
          instanceId,
          payload: {
            menu,
          },
          source: BRIDGE_MESSAGE_SOURCES.FRONTEND,
          target: BRIDGE_MESSAGE_TARGETS.DESKTOP,
          type: DESKTOP_AVATAR_MENU_MESSAGE_TYPES.UPDATE,
        })
      );
    },
    [bridgeRoom, bridgeStatus, instanceId, sendJson]
  );

  const handleAvatarMenuAction = useCallback(
    (payload: JsonMessagePayload) => {
      const action = getAvatarMenuAction({ bridgeRoom, payload });
      if (!action) {
        return;
      }

      if (isDemoUserId(action.userId)) {
        return;
      }

      const participantIdentifiers = toParticipantIdentifierSet({
        currentUserIdentifier: user.email,
        selectedUserIdentifier: action.teamsUserIdentifier,
      });

      Promise.resolve()
        .then(async () => {
          // Force deep link usage for actions from the desktop overlay so that
          // the Teams window is brought to the foreground on Windows.
          const forceDeepLink = { forceDeepLink: true } as const;

          if (action.action === DESKTOP_AVATAR_MENU_ACTIONS.START_CHAT) {
            await navigateToChat(
              user.tenantId,
              participantIdentifiers,
              undefined,
              forceDeepLink
            );
            return;
          }

          if (action.action === DESKTOP_AVATAR_MENU_ACTIONS.START_CALL) {
            await navigateToCall(participantIdentifiers, forceDeepLink);
            return;
          }

          const normalizedMessage = action.message?.trim();
          if (!normalizedMessage) {
            return;
          }

          await sendChatMessage({
            message: normalizedMessage,
            msUserId: action.userId,
          });
          await navigateToChat(
            user.tenantId,
            participantIdentifiers,
            undefined,
            forceDeepLink
          );
        })
        .catch((error: unknown) => {
          recordErrorInSentry(error);
        });
    },
    [bridgeRoom, navigateToCall, navigateToChat, user]
  );

  const handleAvatarMenuBridgeMessage = useCallback(
    (payload: JsonMessagePayload) => {
      const request = getAvatarMenuRequest({ bridgeRoom, payload });
      if (request) {
        resolveAvatarMenuPayload(request.userId)
          .then((menu) => {
            sendAvatarMenuUpdate(menu);
          })
          .catch((error: unknown) => {
            recordErrorInSentry(error);
          });
        return;
      }

      handleAvatarMenuAction(payload);
    },
    [
      bridgeRoom,
      handleAvatarMenuAction,
      resolveAvatarMenuPayload,
      sendAvatarMenuUpdate,
    ]
  );

  useEffect(
    () => onJsonMessage(handleAvatarMenuBridgeMessage),
    [handleAvatarMenuBridgeMessage, onJsonMessage]
  );

  return null;
};
